import React, { useState, useEffect } from "react";
import Pagination from "react-responsive-pagination";
import { toast } from "react-toastify";
import cookie from "react-cookies";
import axios from "utils/axios";
import { Form, Field } from "react-final-form";
import FinalFormInput from "components/FinalFormInput";
import Dropzone from "components/Dropzone";
import { getDate, scrollToTop } from "utils/utils";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import DeleteIcon from "@mui/icons-material/Delete";

type Props = {
  org: string;
};

const OrganizationAd: React.FC<Props> = ({ org }) => {
  const [formData, setFormData] = useState<Array<any>>();
  const [open, setOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [showCount, setShowCount] = useState<number>(10);
  const [editable, setEditable] = useState<boolean>(false);
  const [adField, setAdField] = useState<any>({});
  const [resourceNameList, setResourceNameList] = useState<Array<any>>([]);
  const [file, setFile] = useState<any>({});
  const navigate = useNavigate();

  const loadPage = async () => {
    try {
      const response = await axios.get(
        `/api/ad/readByOrganization/${showCount}/${currentPage}/${org}`
      );
      setTotalPages(Math.ceil(response.data.count / showCount));
      setFormData(response.data.rows);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };

  useEffect(() => {
    scrollToTop();
    org !== "" && loadPage();
  }, [currentPage, showCount]); //eslint-disable-line

  useEffect(() => {
    const loadResourceName = async () => {
      const { data } = await axios.get(
        `/api/resource/getByOrganization/${org}`
      );
      let array: any = [];
      data.map((item: any) => array.push(item.name));
      setResourceNameList(array);
    };
    org !== "" && loadResourceName();
  }, [org]);

  const handlePageChange = (e: number) => {
    setCurrentPage(e);
  };
  const handleCountChange = (e: number) => {
    if (totalPages === 1 && e > showCount) {
      toast.warning(
        "All results displayed! You can see all the ads currently."
      );
      return;
    }
    setCurrentPage(1);
    setShowCount(e);
  };

  const handleRead = (ad: any) => {
    setAdField(ad);
    setEditable(false);
    setOpen(true);
  };

  const handleEdit = (ad: any) => {
    setAdField(ad);
    setFile(ad.image);
    setEditable(true);
    setOpen(true);
  };

  const handleUpdate = async (val: any) => {
    try {
      const result = await axios.put("/api/ad/update", {
        ...val,
        image: file.file,
      });
      loadPage();
      toast.success(result.data.message);
      setOpen(false);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDelete = async (id: number) => {
    Swal.fire({
      title: "<h6>Are you sure you want to delete this ad?</h6>",
      showDenyButton: true,
      showCancelButton: false,
      confirmButtonText: `Confirm`,
      denyButtonText: `Cancel`,
    }).then(async (result) => {
      /* Read more about isConfirmed, isDenied below */
      if (result.isConfirmed) {
        try {
          const result = await axios.delete(`/api/ad/delete/${id}`);
          loadPage();
          toast.success(result.data.message);
          navigate("/dashboard");
        } catch (error: any) {
          toast.error(error.response.data.message);
        }
      } else if (result.isDenied) {
        return;
      }
    });
  };

  const handleCreate = () => {
    window.open("/create-ad", "_blank");
  };

  const handleClick = (errors: any, values: any) => {};

  return (
    <>
      {formData?.length !== 0 && (
        <div className="main-text text-right sm:text-center">
          <span
            className={`cursor-pointer ${
              showCount === 10 ? "text-button font-extrabold" : ""
            }`}
            onClick={() => handleCountChange(10)}
          >
            10
          </span>
          {" | "}
          <span
            className={`cursor-pointer ${
              showCount === 25 ? "text-button font-extrabold" : ""
            }`}
            onClick={() => handleCountChange(25)}
          >
            25
          </span>
          {" | "}
          <span
            className={`cursor-pointer ${
              showCount === 100 ? "text-button font-extrabold" : ""
            }`}
            onClick={() => handleCountChange(100)}
          >
            100
          </span>
        </div>
      )}

      <div className="grid gap-[8px]">
        {formData?.map((ad, index) => (
          <div
            className="px-[20px] py-[8px] flex sm:flex-col items-center justify-between sm:items-start bg-gray main-text border-solid border-[1.5px] border-[#bdbdbd] rounded-sm shadow-md hover:shadow-none cursor-default"
            key={index}
          >
            <span className="flex items-center">
              {ad.title} &nbsp; {ad.resourceName}
              <br />
              Date purchased: {getDate(ad.createdAt)}
            </span>
            <div className="flex sm:ml-auto sm:mt-[20px]">
              <div onClick={() => handleRead(ad)} className="cursor-pointer">
                <VisibilityIcon color="primary" />
              </div>
              {cookie.load("user")?.organization.toLowerCase() === "gyde" && (
                <>
                  <div
                    onClick={() => handleEdit(ad)}
                    className="cursor-pointer"
                  >
                    <ModeEditOutlineIcon color="success" />
                  </div>
                  <div
                    onClick={() => handleDelete(ad.id)}
                    className="cursor-pointer"
                  >
                    <DeleteIcon color="error" />
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
        <Pagination
          total={totalPages}
          current={currentPage}
          onPageChange={(page) => handlePageChange(page)}
          maxWidth={300}
        />
        <Dialog open={open} onClose={handleClose}>
          <Form
            onSubmit={(val) => handleUpdate(val)}
            initialValues={adField}
            render={({ handleSubmit, errors, values }) => (
              <form onSubmit={handleSubmit} autoComplete="off">
                <DialogTitle>Ad Detail</DialogTitle>
                <DialogContent>
                  <div className="grid gap-[12px] mt-[12px] w-[480px] sm:w-full">
                    <Field
                      id="title"
                      name="title"
                      placeholder="Title"
                      render={(props: any) => (
                        <FinalFormInput
                          {...props}
                          readOnly={!editable}
                          required
                        />
                      )}
                    ></Field>
                    <Field
                      id="resourceName"
                      name="resourceName"
                      placeholder="Resource Name"
                    >
                      {(props: any) => (
                        <FinalFormInput
                          {...props}
                          component="autocomplete"
                          options={resourceNameList}
                          defaultValue={adField.resourceName}
                          readOnly={!editable}
                          required
                        />
                      )}
                    </Field>
                    <Field
                      id="websiteLink"
                      name="websiteLink"
                      placeholder="Website Link"
                    >
                      {(props: any) => (
                        <FinalFormInput
                          {...props}
                          readOnly={!editable}
                          required
                        />
                      )}
                    </Field>
                    <Field
                      id="description"
                      name="description"
                      placeholder="Description"
                      rows={8}
                    >
                      {(props: any) => (
                        <FinalFormInput
                          {...props}
                          component="textarea"
                          readOnly={!editable}
                          required
                        />
                      )}
                    </Field>
                    {editable ? (
                      <Dropzone
                        acceptType={{ "image/*": [] }}
                        setFile={(f: any, type: any) => {
                          setFile({ file: f, type: type });
                        }}
                        defaultFile={file || ""}
                      />
                    ) : (
                      <img src={adField.image} alt="logo" className="w-full" />
                    )}
                  </div>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose}>
                    {editable ? "Cancel" : "Close"}
                  </Button>
                  {editable && (
                    <Button
                      type="submit"
                      onClick={() => handleClick(errors, values)}
                    >
                      Update
                    </Button>
                  )}
                </DialogActions>
              </form>
            )}
          />
        </Dialog>
      </div>
      {cookie.load("user") &&
        (cookie.load("user").userType === "admin" ||
          cookie.load("user").userType === "super_user") &&
        cookie.load("user").organization.toLowerCase() === "gyde" && (
          <button
            className="high-emphasis ml-auto mt-[16px]"
            onClick={handleCreate}
          >
            CREATE AN AD
          </button>
        )}
    </>
  );
};

export default OrganizationAd;
